// Roles & permissions editor (Normal Core MASTER-PRD §6.5.6, #918). The
// human surface over the Phase-4 capability graph (#916): builtin + custom
// roles with their capability grants, actor→role assignment (which is what
// ENGAGES enforcement — a pristine workspace behaves permissively), and the
// read-only per-entity ACL inspector. All data flows through the `security`
// contract — no raw fetch. Mutations are owner-gated in the routes.

import { useState } from "react";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  DataTable,
  Input,
  IntentBadge,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SettingsSection,
} from "../../index";
import { useApi, apiCall, ApiError } from "../../api/client";
import {
  security,
  type SecurityActor,
  type SecurityAclRow,
  type SecurityRole,
} from "../../api/contracts/security";

const UNASSIGNED = "__none__";

export function RoleGrantsEditor() {
  const rolesQuery = useApi(security.roles, {});
  const [error, setError] = useState<string | null>(null);
  const [draftCapability, setDraftCapability] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  const data = rolesQuery.data;

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await rolesQuery.mutate();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const seedRoles = () => act(() => apiCall(security.seedRoles, {}));
  const assignRole = (actorExternalId: string, roleSlug: string | null) =>
    act(() => apiCall(security.assignRole, { body: { actorExternalId, roleSlug } }));
  const grant = (roleSlug: string, capability: string, remove: boolean) =>
    act(async () => {
      const r = await apiCall(security.grant, { body: { roleSlug, capability, remove } });
      if (!r.ok) throw new ApiError(400, r.reason ?? "grant refused");
    });

  if (rolesQuery.isLoading || !data) {
    return (
      <SettingsSection title="Roles & permissions" description="Loading the capability graph…">
        <div className="type-body text-muted-foreground">Loading…</div>
      </SettingsSection>
    );
  }

  return (
    <div className="space-y-6">
      {error ? (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      <SettingsSection
        title="Enforcement"
        description="How the capability graph applies to this workspace."
        id="enforcement"
      >
        {data.engaged ? (
          <Alert>
            <AlertTitle className="flex items-center gap-2">
              <IntentBadge intent="success" withDot>
                Enforced
              </IntentBadge>
            </AlertTitle>
            <AlertDescription>
              Roles are assigned, so mutations resolve capabilities: an owner (or a
              grant) edits anything; a member edits their own work; everything else
              is refused with a typed reason.
            </AlertDescription>
          </Alert>
        ) : (
          <Alert>
            <AlertTitle className="flex items-center gap-2">
              <IntentBadge intent="neutral" withDot>
                Not enforced
              </IntentBadge>
            </AlertTitle>
            <AlertDescription className="space-y-2">
              <span>
                No actor holds a role yet, so every mutation is allowed — the
                single-player default. Assigning the first role below switches
                enforcement on for this workspace.
              </span>
              {data.roles.length === 0 ? (
                <Button variant="outline" onClick={seedRoles} disabled={busy}>
                  Create the built-in roles
                </Button>
              ) : null}
            </AlertDescription>
          </Alert>
        )}
      </SettingsSection>

      {data.roles.length > 0 ? (
        <SettingsSection
          title="Roles"
          description="Capability grants per role. Wildcards match per segment — `*` is everything, `*:mutate:own` is “mutate anything you own”."
          id="roles"
        >
          <div className="space-y-0">
            {data.roles.map((role: SecurityRole) => (
              <div
                key={role.uid}
                className="border-b border-border/40 py-4 first:pt-0 last:border-b-0 last:pb-0"
              >
                <div className="flex items-center gap-2">
                  <span className="type-body font-medium">{role.name}</span>
                  {role.builtin ? <Badge variant="outline">built-in</Badge> : null}
                  <span className="type-subtext text-muted-foreground">
                    {role.actorCount} actor{role.actorCount === 1 ? "" : "s"}
                  </span>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  {role.grants.map((cap) => (
                    <Badge key={cap} variant="outline" className="gap-1 font-mono">
                      {cap}
                      <button
                        type="button"
                        aria-label={`Remove ${cap} from ${role.name}`}
                        className="text-muted-foreground hover:text-foreground"
                        onClick={() => grant(role.slug, cap, true)}
                        disabled={busy}
                      >
                        ×
                      </button>
                    </Badge>
                  ))}
                  {role.grants.length === 0 ? (
                    <span className="type-subtext text-muted-foreground">
                      No grants — this role can do nothing.
                    </span>
                  ) : null}
                </div>
                <form
                  className="mt-2 flex items-center gap-2"
                  onSubmit={(e) => {
                    e.preventDefault();
                    const cap = (draftCapability[role.slug] ?? "").trim();
                    if (!cap) return;
                    void grant(role.slug, cap, false).then(() =>
                      setDraftCapability((d) => ({ ...d, [role.slug]: "" })),
                    );
                  }}
                >
                  <Input
                    value={draftCapability[role.slug] ?? ""}
                    onChange={(e) =>
                      setDraftCapability((d) => ({ ...d, [role.slug]: e.target.value }))
                    }
                    placeholder="task:mutate:any"
                    className="h-7 w-56 font-mono type-subtext"
                  />
                  <Button type="submit" variant="ghost" disabled={busy}>
                    Add grant
                  </Button>
                </form>
              </div>
            ))}
          </div>
        </SettingsSection>
      ) : null}

      <SettingsSection
        title="Actor roles"
        description="Who holds which role. Assigning the first role engages enforcement."
        id="actor-roles"
      >
        <DataTable<SecurityActor>
          wrapInCard={false}
          rows={data.actors}
          rowKey={(a) => a.externalId}
          empty="No actors in this workspace yet."
          columns={[
            {
              key: "actor",
              header: "Actor",
              render: (a) => (
                <span className="font-mono type-subtext">
                  {a.displayName ?? a.externalId}
                </span>
              ),
            },
            { key: "kind", header: "Kind", render: (a) => a.kind },
            {
              key: "role",
              header: "Role",
              width: "w-48",
              render: (a) => (
                <Select
                  value={a.roleSlug ?? UNASSIGNED}
                  onValueChange={(v) =>
                    assignRole(a.externalId, v === UNASSIGNED ? null : v)
                  }
                  disabled={busy || data.roles.length === 0}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="No role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={UNASSIGNED}>No role</SelectItem>
                    {data.roles.map((r) => (
                      <SelectItem key={r.slug} value={r.slug}>
                        {r.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ),
            },
          ]}
        />
      </SettingsSection>

      <SettingsSection
        title="Per-entity access"
        description="Direct grants on specific entities (entity ACL) — the escape hatch when a role is too coarse. Granted by agents/tools; inspected here."
        id="entity-acl"
      >
        <DataTable<SecurityAclRow>
          wrapInCard={false}
          rows={data.acl}
          rowKey={(r, i) => `${r.entityKind}:${r.entityUid}:${r.capability}:${i}`}
          empty="No per-entity grants."
          columns={[
            { key: "entity", header: "Entity", render: (r) => (
              <span className="font-mono type-subtext">{r.entityKind}:{r.entityUid.slice(0, 10)}…</span>
            ) },
            {
              key: "grantee",
              header: "Grantee",
              render: (r) =>
                r.actorExternalId ? (
                  <span className="font-mono type-subtext">{r.actorExternalId}</span>
                ) : (
                  <span>{r.roleSlug ?? "—"}</span>
                ),
            },
            { key: "capability", header: "Capability", render: (r) => (
              <Badge variant="outline" className="font-mono">{r.capability}</Badge>
            ) },
          ]}
        />
      </SettingsSection>
    </div>
  );
}
